import { useCallback, useState } from 'react';
import { useParams } from 'react-router-dom';

import { GridRowParams } from '@mui/x-data-grid-pro';
import { v4 as uuidv4 } from 'uuid';

import { InmateApi } from '@/api';
import { DataGrid } from '@/components';
import { useDataGridColumns } from '@/components/DataGrid/hooks/useDataGridCols';
import {
  DataGridColSettings,
  mergeColumnSettings,
} from '@/components/DataGrid/utils';
import { Button } from '@/components/TailwindComponents';
import { LocalStoragePrefix } from '@/constants';
import AddInmateNoteSidePanel from '@/pages/JMS/Managament/components/panels/AddInmateNoteSidePanel';
import { InmateNotesGridColumns } from '@/pages/JMS/Managament/data/data';
import { IInmateNote } from '@/pages/JMS/Managament/types/inmate';
import { useUserStore, useToastStore } from '@/store';
import { getLocalStorageItem } from '@/utils';

export const InmateNotesDataGrid = () => {
  const { inmateId } = useParams<{
    inmateId: string;
  }>();

  const { user } = useUserStore();
  const { updateToast } = useToastStore();

  const [refreshKey, setRefreshKey] = useState(0);
  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const [currentNote, setCurrentNote] = useState<IInmateNote | null>(null);

  const fetchNotes = useCallback(
    async (
      page: number,
      limit: number,
      filter: string,
    ): Promise<{ rows: any[]; totalCount: number }> => {
      try {
        const response = await InmateApi.noteList(
          {
            page,
            limit,
            filter,
            sort: JSON.stringify({ createdAt: -1 }),
          },
          inmateId,
        );
        const { data } = response;

        return {
          rows: data.docs,
          totalCount: data.totalDocs,
        };
      } catch (err: any) {
        updateToast({ open: true, message: err.message });
        return { rows: [], totalCount: 0 };
      }
    },
    [inmateId],
  );

  const [columnSettings, setColumnSettings] = useState<DataGridColSettings[]>(
    () => {
      const savedSettings = getLocalStorageItem({
        key: String(user?._id),
        prefix: `inmate-notes-${LocalStoragePrefix.TableSetting}`,
        shouldParse: true,
      });

      if (!savedSettings) {
        return InmateNotesGridColumns;
      }

      return mergeColumnSettings(InmateNotesGridColumns, savedSettings);
    },
  );

  const columns = useDataGridColumns({
    type: 'inmateNotes',
    columnSettings,
  });

  const handleAddNote = () => {
    setIsPanelOpen(true);
  };

  const cta = (
    <Button className="h-[38px]" onClick={handleAddNote}>
      Add Note
    </Button>
  );

  const handleRowClick = (inmateNote: IInmateNote) => {
    setCurrentNote(inmateNote);
    setIsPanelOpen(true);
  };

  return (
    <>
      <DataGrid
        name="inmate-notes"
        key={refreshKey}
        columns={columns}
        columnSettings={columnSettings}
        setColumnSettings={setColumnSettings}
        fetchData={fetchNotes}
        cta={cta}
        onRowClick={(e: GridRowParams<IInmateNote>) => handleRowClick(e.row)}
      />
      <AddInmateNoteSidePanel
        key={uuidv4()}
        setCurrentNote={setCurrentNote}
        currentNote={currentNote}
        setRefreshKey={setRefreshKey}
        isOpen={isPanelOpen}
        setIsOpen={setIsPanelOpen}
      />
    </>
  );
};
